import { useMemo, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import type { Taak, TaakStatus } from '../../../gedeeld/types'
import { api } from '../../lib/api'
import { STATUSSEN, opDringendheid } from '../../lib/opmaak'
import { meldFout, openTaak, taakGewijzigd, useBasis } from '../../lib/staat'
import { Kies } from '../ui'
import { TaakKaart } from '../taak/stukjes'
import type { ProjectContext } from '../ProjectScherm'

/* ===========================================================================
 *  Het bord
 *
 *  Vier vaste kolommen, dezelfde als in Werk: Te doen, Bezig, Wacht, Klaar.
 *  Geen eigen kolommen per project, en dat is overgenomen uit 0067: zodra
 *  het ene bord "Review" heeft en het andere "Testen", betekent "klaar" op
 *  het overzicht niets meer -- en in het dashboard moet een taak in één van
 *  die vier staan. Wie "in review" wil zien, zet een label.
 *
 *  Slepen verandert de status en de plek in de kolom, en verder niets. De
 *  plek wordt voor de hele doelkolom opnieuw genummerd, net als in Werk,
 *  zodat twee kaarten nooit hetzelfde nummer hebben en de volgorde niet
 *  omklapt.
 *
 *  Klaar laat standaard de laatste twee weken zien. Een bord met
 *  vierhonderd afgeronde kaarten is een bord waar je naar de andere drie
 *  kolommen moet zoeken.
 * =========================================================================== */

type Banen = 'geen' | 'persoon' | 'epic'

export default function Kanban({ ctx }: { ctx: ProjectContext }) {
  const { projecten } = useBasis()
  const [banen, setBanen] = useState<Banen>('geen')
  const [allesKlaar, setAllesKlaar] = useState(false)
  const sleep = useRef<string | null>(null)
  const [boven, setBoven] = useState<{ status: TaakStatus; baan: string; index: number } | null>(null)

  const grens = Date.now() - 14 * 86400000
  const zicht = ctx.zicht.filter((t) => t.status !== 'klaar' || allesKlaar || (t.klaarAt ?? t.updatedAt) > grens)
  const verborgenKlaar = ctx.zicht.filter((t) => t.status === 'klaar').length - zicht.filter((t) => t.status === 'klaar').length

  /* Subtaken tellen op de kaart van hun ouder. */
  const kinderen = useMemo(() => {
    const m = new Map<string, { klaar: number; totaal: number }>()
    for (const t of ctx.taken) {
      if (!t.ouderId) continue
      const x = m.get(t.ouderId) ?? { klaar: 0, totaal: 0 }
      x.totaal++
      if (t.status === 'klaar') x.klaar++
      m.set(t.ouderId, x)
    }
    return m
  }, [ctx.taken])

  const epics = ctx.taken.filter((t) => t.soort === 'epic')

  const baanVan = (t: Taak): string =>
    banen === 'persoon' ? (t.toegewezenAan ?? '-') : banen === 'epic' ? (t.soort === 'epic' ? t.id : t.ouderId ?? '-') : 'alles'

  const banenLijst = useMemo(() => {
    if (banen === 'geen') return [{ key: 'alles', naam: '' }]
    const keys = [...new Set(zicht.map(baanVan))]
    return keys.map((k) => ({
      key: k,
      naam: k === '-' ? (banen === 'persoon' ? 'Niemand' : 'Zonder epic')
        : banen === 'persoon' ? (zicht.find((t) => t.toegewezenAan === k)?.toegewezenNaam ?? 'Onbekend')
          : (epics.find((e) => e.id === k)?.titel ?? 'Epic'),
    })).sort((a, b) => (a.key === '-' ? 1 : b.key === '-' ? -1 : a.naam.localeCompare(b.naam)))
  }, [zicht, banen, epics])

  function kolom(status: TaakStatus, baan: string): Taak[] {
    return zicht
      .filter((t) => t.status === status && baanVan(t) === baan)
      .sort((a, b) => a.volgorde - b.volgorde || opDringendheid(a, b))
  }

  async function laatVallen(status: TaakStatus, baan: string, index: number) {
    const id = sleep.current
    sleep.current = null
    setBoven(null)
    if (!id) return
    const taak = ctx.taken.find((t) => t.id === id)
    if (!taak) return

    /* De doelkolom zonder de gesleepte kaart, en hem op zijn nieuwe plek. */
    const doel = kolom(status, baan).filter((t) => t.id !== id)
    doel.splice(Math.min(index, doel.length), 0, taak)

    const extra: Partial<Taak> = {}
    if (banen === 'persoon' && baan !== baanVan(taak)) {
      extra.toegewezenAan = baan === '-' ? null : baan
    }
    if (banen === 'epic' && baan !== baanVan(taak) && taak.soort !== 'epic') {
      extra.ouderId = baan === '-' ? null : baan
    }

    const zetten = doel.map((t, i) => ({ id: t.id, status, volgorde: i }))
      .filter((z) => {
        const t = ctx.taken.find((x) => x.id === z.id)!
        return t.status !== z.status || t.volgorde !== z.volgorde
      })
    if (!zetten.length && !Object.keys(extra).length) return

    /* Alvast op het scherm; terug als de server nee zegt. */
    const voor = ctx.taken
    ctx.setTaken((lijst) => lijst.map((t) => {
      const z = zetten.find((x) => x.id === t.id)
      return z ? { ...t, status, volgorde: z.volgorde, ...(t.id === id ? extra : {}) } : t.id === id ? { ...t, ...extra } : t
    }))

    try {
      if (zetten.length) await api.takenVerplaatsen(zetten)
      if (Object.keys(extra).length) {
        const na = await api.taakOpslaan({ id, ...extra })
        taakGewijzigd(na)
      }
    } catch (e) {
      ctx.setTaken(() => voor)
      meldFout(e)
    }
  }

  function overKolom(e: React.DragEvent, status: TaakStatus, baan: string) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    /* Waar in de kolom: tellen hoeveel kaarten boven de muis eindigen. */
    const kaarten = [...(e.currentTarget as HTMLElement).querySelectorAll<HTMLElement>('.kaart')]
      .filter((k) => k.dataset.taak !== sleep.current)
    let index = kaarten.length
    for (let i = 0; i < kaarten.length; i++) {
      const r = kaarten[i]!.getBoundingClientRect()
      if (e.clientY < r.top + r.height / 2) { index = i; break }
    }
    if (!boven || boven.status !== status || boven.baan !== baan || boven.index !== index) setBoven({ status, baan, index })
  }

  return (
    <div className="kanban-wrap">
      <div className="werkbalk dun">
        <span className="stil">Banen</span>
        <Kies<Banen> klein waarde={banen} onKies={setBanen} opties={[
          { key: 'geen', label: 'Geen' },
          { key: 'persoon', label: 'Per persoon' },
          { key: 'epic', label: 'Per epic' },
        ]} />
        <span className="vul" />
        {(verborgenKlaar > 0 || allesKlaar) && (
          <button type="button" className="link-knop" onClick={() => setAllesKlaar((x) => !x)}>
            {allesKlaar ? 'Alleen recent klaar' : `Ook ${verborgenKlaar} oudere klaar tonen`}
          </button>
        )}
      </div>

      {banenLijst.map((b) => (
        <div key={b.key} className="baan">
          {b.naam && <h3 className="baan-kop">{b.naam}<span className="stil">{zicht.filter((t) => baanVan(t) === b.key).length}</span></h3>}
          <div className="kanban">
            {STATUSSEN.map((s) => {
              const lijst = kolom(s.key, b.key)
              const punten = lijst.reduce((n, t) => n + (t.punten ?? 0), 0)
              return (
                <div key={s.key}
                  className={`kolom ${boven?.status === s.key && boven.baan === b.key ? 'erboven' : ''}`}
                  onDragOver={(e) => overKolom(e, s.key, b.key)}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setBoven(null) }}
                  onDrop={(e) => { e.preventDefault(); void laatVallen(s.key, b.key, boven?.index ?? lijst.length) }}>
                  <div className="kolom-kop">
                    <span className={`punt t-${s.kleur}`} />
                    <strong>{s.label}</strong>
                    <span className="teller">{lijst.length}</span>
                    <span className="vul" />
                    {punten > 0 && <span className="stil" title="Story points">{punten}p</span>}
                  </div>
                  <div className="kolom-lijf">
                    {lijst.map((t, i) => (
                      <div key={t.id}>
                        {boven?.status === s.key && boven.baan === b.key && boven.index === i && <div className="valplek" />}
                        <TaakKaart taak={t} projecten={projecten} project={ctx.project}
                          subtaken={kinderen.get(t.id)}
                          sleepbaar onSleep={(e) => {
                            sleep.current = t.id
                            e.dataTransfer.effectAllowed = 'move'
                            e.dataTransfer.setData('text/plain', t.id)
                          }}
                          onOpen={() => openTaak(t.id)} />
                      </div>
                    ))}
                    {boven?.status === s.key && boven.baan === b.key && boven.index >= lijst.length && <div className="valplek" />}
                    <SnelToevoegen ctx={ctx} status={s.key} baan={banen === 'persoon' && b.key !== '-' ? b.key : null}
                      ouder={banen === 'epic' && b.key !== '-' ? b.key : null} volgorde={lijst.length} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function SnelToevoegen({ ctx, status, baan, ouder, volgorde }: {
  ctx: ProjectContext; status: TaakStatus; baan: string | null; ouder: string | null; volgorde: number
}) {
  const [open, setOpen] = useState(false)
  const [titel, setTitel] = useState('')
  const [bezig, setBezig] = useState(false)

  async function maak() {
    const t = titel.trim()
    if (!t || bezig) return
    setBezig(true)
    try {
      const nieuw = await api.taakOpslaan({
        titel: t, status, projectId: ctx.project.id, volgorde,
        toegewezenAan: baan, ouderId: ouder,
        sprintId: ctx.actieveSprint?.id ?? null,
      })
      taakGewijzigd(nieuw)
      setTitel('')
    } catch (e) { meldFout(e) }
    setBezig(false)
  }

  if (!open) {
    return (
      <button type="button" className="snel-toevoegen" onClick={() => setOpen(true)}>
        <Plus size={14} /> Kaart toevoegen
      </button>
    )
  }
  return (
    <div className="snel-invoer">
      <textarea autoFocus className="invoer" rows={2} value={titel} disabled={bezig}
        placeholder={ctx.actieveSprint ? `Komt in ${ctx.actieveSprint.naam}. Enter = toevoegen` : 'Enter = toevoegen, Esc = stoppen'}
        onChange={(e) => setTitel(e.target.value)}
        onBlur={() => { if (!titel.trim()) setOpen(false) }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void maak() }
          if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); setTitel('') }
        }} />
    </div>
  )
}
